import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QrCode } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonLink, Checkbox, Dialog, Input, Select } from '@artosera/ui';
import {
  BLOCS_CARTEL_RICHE,
  estFormatCartel,
  FORMATS_ETIQUETTE,
  type BlocCartelRiche,
  type FormatEtiquette,
} from '@artosera/shared';
import { reserveApi, useMutationReserve } from '../../api/reserve.js';
import { enregistrerFichier, type FichierTelecharge } from '../../api/client.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import styles from './ImpressionEtiquettes.module.css';

/** Au-delà, l'API refuse le lot : le bouton ne s'offre pas. */
export const ETIQUETTES_PAR_LOT = 200;

/**
 * Étiquettes QR et cartels muraux d'une ou plusieurs œuvres, avec aperçu du
 * PDF avant de l'enregistrer. Le QR d'une étiquette ouvre la fiche dans la
 * régie ; celui d'un cartel, la fiche publique de l'œuvre.
 */
export function BoutonEtiquettes({ oeuvreIds }: { oeuvreIds: string[] }) {
  const { t } = useTranslation('regie');
  const { galerie } = useShellContext();
  const [ouvert, setOuvert] = useState(false);
  if (
    oeuvreIds.length === 0 ||
    oeuvreIds.length > ETIQUETTES_PAR_LOT ||
    !galerieALaction(galerie, 'artworks:read')
  )
    return null;
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOuvert(true)}>
        <QrCode size={16} strokeWidth={1.5} aria-hidden />
        {t('etiquettes.action')}
        {oeuvreIds.length > 1 ? ` — ${String(oeuvreIds.length)}` : null}
      </Button>
      {ouvert ? (
        <ImpressionEtiquettes oeuvreIds={oeuvreIds} onFermer={() => setOuvert(false)} />
      ) : null}
    </>
  );
}

function ImpressionEtiquettes({
  oeuvreIds,
  onFermer,
}: {
  oeuvreIds: string[];
  onFermer: () => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const peutEcrire = galerieALaction(galerie, 'artworks:write');
  const [format, setFormat] = useState<FormatEtiquette>('rouleau-62x29');
  const [texteLibre, setTexteLibre] = useState('');
  const [afficherPrix, setAfficherPrix] = useState(false);
  const [sans, setSans] = useState<readonly BlocCartelRiche[]>([]);
  const [apercu, setApercu] = useState<{ fichier: FichierTelecharge; url: string } | null>(null);
  const cartel = estFormatCartel(format);
  const riche = format === 'cartel-riche-a6';

  const etat = useQuery({
    queryKey: cleMetier(galerieSlug, 'oeuvres', 'cartels', oeuvreIds),
    queryFn: (c) => reserveApi.etatCartels(galerieSlug, oeuvreIds, appelDeRequete(c)),
    enabled: cartel,
  });
  const nonPubliables = etat.data?.cartels.filter((c) => !c.publiable).length ?? 0;
  const cartelActif = oeuvreIds.length === 1 && etat.data?.cartels[0]?.actif === true;

  const pdf = useMutationReserve(galerieSlug, (_: void, appel) =>
    reserveApi.pdfEtiquettes(
      galerieSlug,
      {
        cibles: oeuvreIds.map((id) => ({ type: 'oeuvre' as const, id })),
        format,
        ...(cartel ? { afficherPrix } : { texteLibre }),
        ...(riche && sans.length > 0 ? { riche: { sans: [...sans] } } : {}),
      },
      i18n.resolvedLanguage ?? 'fr',
      appel,
    ),
  );
  const revoquer = useMutationReserve(galerieSlug, (_: void, appel) =>
    reserveApi.revoquerCartel(galerieSlug, oeuvreIds[0]!, appel),
  );

  const [confirmation, setConfirmation] = useState(false);

  // Un réglage changé rend l'aperçu caduc ; son adresse locale est libérée.
  function regler<T>(poser: (valeur: T) => void) {
    return (valeur: T) => {
      poser(valeur);
      setApercu(null);
    };
  }
  useEffect(
    () => () => {
      if (apercu) URL.revokeObjectURL(apercu.url);
    },
    [apercu],
  );

  const erreur = pdf.error ?? revoquer.error;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onFermer()}
      size={apercu ? 'full' : 'md'}
      title={t('regie:etiquettes.titre', { count: oeuvreIds.length })}
      closeLabel={t('commun:actions.fermer')}
    >
      <div className={styles.formulaire}>
        <Field htmlFor="impression-format" label={t('regie:inventaire.format')}>
          <Select
            id="impression-format"
            value={format}
            onValueChange={(v) => regler(setFormat)(v as FormatEtiquette)}
            options={FORMATS_ETIQUETTE.map((f) => ({
              value: f,
              label: t(`regie:inventaire.formats.${f}`),
            }))}
          />
        </Field>
        <p className={styles.aide}>
          {cartel ? t('regie:etiquettes.aideCartel') : t('regie:etiquettes.aideInterne')}
        </p>
        {cartel ? (
          <>
            {peutEcrire ? (
              <Checkbox
                id="impression-prix"
                label={t('regie:etiquettes.afficherPrix')}
                checked={afficherPrix}
                onCheckedChange={(c) => regler(setAfficherPrix)(c === true)}
              />
            ) : null}
            {riche ? (
              <fieldset className={styles.formulaire}>
                <legend>{t('regie:etiquettes.riche.titre')}</legend>
                <p className={styles.aide}>{t('regie:etiquettes.riche.aide')}</p>
                {BLOCS_CARTEL_RICHE.map((bloc) => (
                  <Checkbox
                    key={bloc}
                    id={`impression-riche-${bloc}`}
                    label={t(`regie:etiquettes.riche.${bloc}`)}
                    checked={!sans.includes(bloc)}
                    onCheckedChange={(coche) =>
                      regler(setSans)(
                        coche === true ? sans.filter((b) => b !== bloc) : [...sans, bloc],
                      )
                    }
                  />
                ))}
              </fieldset>
            ) : null}
            {nonPubliables > 0 ? (
              <p className={styles.avertissement} role="status">
                {t('regie:etiquettes.nonPubliables', { count: nonPubliables })}
              </p>
            ) : null}
          </>
        ) : (
          <Field htmlFor="impression-texte" label={t('regie:inventaire.texteLibre')}>
            <Input
              id="impression-texte"
              maxLength={200}
              value={texteLibre}
              onChange={(e) => regler(setTexteLibre)(e.target.value)}
            />
          </Field>
        )}
        <div className={styles.actions}>
          <Button
            loading={pdf.isPending}
            onClick={() =>
              pdf.mutate(undefined, {
                onSuccess: (fichier) =>
                  setApercu({ fichier, url: URL.createObjectURL(fichier.blob) }),
              })
            }
          >
            {t('regie:etiquettes.apercu')}
          </Button>
          {apercu ? (
            <>
              <Button
                variant="secondary"
                onClick={() => enregistrerFichier(apercu.fichier, 'etiquettes.pdf')}
              >
                {t('regie:inventaire.telecharger')}
              </Button>
              <ButtonLink variant="secondary" href={apercu.url} target="_blank" rel="noopener">
                {t('regie:etiquettes.ouvrirPourImprimer')}
              </ButtonLink>
            </>
          ) : null}
          {cartel && cartelActif && peutEcrire && !confirmation ? (
            <Button variant="danger" onClick={() => setConfirmation(true)}>
              {t('regie:etiquettes.revoquer')}
            </Button>
          ) : null}
        </div>
        {cartel && cartelActif && confirmation ? (
          <div className={styles.avertissement}>
            <p>{t('regie:etiquettes.revoquerConfirmation')}</p>
            <div className={styles.actions}>
              <Button
                variant="danger"
                loading={revoquer.isPending}
                onClick={() =>
                  revoquer.mutate(undefined, {
                    onSuccess: () => {
                      setConfirmation(false);
                      setApercu(null);
                      void etat.refetch();
                    },
                  })
                }
              >
                {t('regie:etiquettes.confirmerRevocation')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirmation(false)}>
                {t('commun:actions.annuler')}
              </Button>
            </div>
          </div>
        ) : null}
        {erreur ? (
          <p role="alert">{estApiError(erreur) ? erreur.message : t('commun:erreurReseau')}</p>
        ) : null}
        {apercu ? (
          <iframe
            className={styles.apercu}
            src={apercu.url}
            title={t('regie:etiquettes.apercuTitre')}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
